import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {FontVariant, ThemeProvider} from '@pexip/components';

import {TestId} from '../../../test/testIds';
import {Modal} from '../../components/Modal';
import type {Idp, RegistrationStep} from '../../types';
import {RegistrationError} from '../../types';
import {Register} from '../Register/Register.view';
import {RegistrationExpired} from '../RegistrationExpired/RegistrationExpired.view';
import {RegistrationSuccess} from '../RegistrationSuccess/RegistrationSuccess.view';
import {SelectIdp} from '../SelectIdp/SelectIdp.view';
import {TurnOnNotifications} from '../TurnOnNotifications/TurnOnNotifications.view';

import styles from './Registration.module.scss';

export const RegistrationView: React.FC<{
    error: RegistrationError | undefined;
    isOpen: boolean;
    isLoading: boolean;
    step: RegistrationStep;
    idps: Idp[];
    handleRegisterSubmit: (deviceAlias: string) => Promise<void>;
    handleIdp: (
        chosenIdp: string,
        redirectUrl?: string,
        disablePopupFlow?: boolean,
    ) => Promise<void>;
    handleNotificationsSubmit: () => Promise<void>;
    handleNotificationsSkip: () => Promise<void>;
    handleReRegister: () => void;
    handleSignOut: () => void;
    handleClose: () => void;
}> = ({
    error,
    isOpen,
    isLoading,
    step,
    idps,
    handleRegisterSubmit,
    handleIdp,
    handleNotificationsSubmit,
    handleNotificationsSkip,
    handleReRegister,
    handleSignOut,
    handleClose,
}) => {
    const {t} = useTranslation();

    const errorMessage = useMemo(() => {
        switch (error) {
            case RegistrationError.InvalidVideoAddress:
                return t(
                    'register.invalid-video-address',
                    'Invalid video address. Please check the address and try again.',
                );
            case RegistrationError.AuthFailed:
                return t('register.auth-failed', 'Authentication failed');
            case RegistrationError.RegistrationFailed:
                return t('register.registration-failed', 'Registration failed');
            case RegistrationError.SsoAuthFailed:
                return t(
                    'register.sso-auth-failed',
                    'SSO Authentication Failed',
                );
            case RegistrationError.SsoUnavailable:
                return t(
                    'register.sso-unavailable',
                    'SSO Authentication Failed. The system is in Maintenance mode',
                );
            case RegistrationError.UnexpectedError:
                return t(
                    'register.unexpected-error',
                    'An unexpected error occurred',
                );
            default:
                return undefined;
        }
    }, [error, t]);

    const renderStep = () => {
        switch (step) {
            case 'register':
                return (
                    <Register
                        onSubmit={handleRegisterSubmit}
                        isLoading={isLoading}
                        error={errorMessage}
                    />
                );
            case 'select-idp':
                return (
                    <SelectIdp
                        handleIdp={handleIdp}
                        idps={idps}
                        headingFontVariant={FontVariant.H2}
                        descriptionFontVariant={FontVariant.Body}
                        descriptionVariant="standard"
                        headingText={t(
                            'idp.sign-in-with-sso',
                            'Sign in with SSO',
                        )}
                        descriptionText={t(
                            'idp.please-choose-your-identity-provider',
                            'Please choose your identity provider.',
                        )}
                        descriptionClassName="mb-2 d-inline-block"
                    />
                );
            case 'notifications':
                return (
                    <TurnOnNotifications
                        onSubmit={handleNotificationsSubmit}
                        onSkip={handleNotificationsSkip}
                    />
                );
            case 'expired':
                return (
                    <RegistrationExpired
                        onReRegister={handleReRegister}
                        onSignOut={handleSignOut}
                        isLoading={isLoading}
                    />
                );
            case 'success':
                return <RegistrationSuccess onDone={handleClose} />;
            default:
                return null;
        }
    };

    return (
        <ThemeProvider colorScheme="light">
            <Modal
                className={styles.modal}
                uniqueTitle={t('register.modal-title', 'Registration')}
                isOpen={isOpen}
                onClose={handleClose}
                withCloseButton
                sizeModifier="none"
                data-testid={TestId.ModalRegistration}
            >
                {renderStep()}
            </Modal>
        </ThemeProvider>
    );
};

export type RegistrationViewProps = React.ComponentProps<
    typeof RegistrationView
>;
